import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";
import styled from "styled-components";

import {
  Button,
  Card,
  DesignSystemSurface,
  EmptyState,
  PageHeader,
  Skeleton,
  Tag,
} from "../../../design-system";
import StoreUser from "../../../types/StoreUser";
import {
  getLeadMagnets,
  getLeadMagnetLeads,
  LeadMagnetSummary,
  LeadMagnetCapture,
  LeadMagnetDailyStat,
} from "../../../database/leadMagnets/leadMagnetsApi";

const Page = styled.div`
  min-height: calc(100vh - 104px);
  padding: var(--space-6) var(--space-6) var(--space-16);
`;

const PageInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: var(--space-6);
`;

const TableHead = styled.div`
  display: grid;
  grid-template-columns: 2.2fr 0.7fr 0.8fr 0.6fr 0.9fr 1.1fr 1.1fr;
  gap: var(--space-3);
  padding: var(--space-2) var(--space-4);
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--color-text-secondary);
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 2.2fr 0.7fr 0.8fr 0.6fr 0.9fr 1.1fr 1.1fr;
  gap: var(--space-3);
  align-items: center;
  padding: var(--space-3) var(--space-4);
  border-top: 1px solid var(--color-border);
  cursor: pointer;

  &:hover {
    background: var(--color-surface-hover, rgba(0, 0, 0, 0.03));
  }
`;

const NameCell = styled.div`
  min-width: 0;

  strong {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  span {
    display: block;
    font-size: 12px;
    color: var(--color-text-secondary);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const Num = styled.div`
  font-variant-numeric: tabular-nums;
`;

const Actions = styled.div`
  display: flex;
  gap: var(--space-2);
  justify-content: flex-end;
`;

const CapturesPanel = styled.div`
  padding: var(--space-3) var(--space-4) var(--space-4);
  border-top: 1px dashed var(--color-border);
  background: var(--color-surface-subtle, rgba(0, 0, 0, 0.02));
`;

const PanelMeta = styled.div`
  font-size: 12px;
  color: var(--color-text-secondary);
  margin-bottom: var(--space-3);
`;

const TrendBlock = styled.div`
  margin-bottom: var(--space-4);

  > strong {
    display: block;
    font-size: 13px;
    margin-bottom: var(--space-1);
  }
`;

const CaptureRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: var(--space-3);
  padding: var(--space-1) 0;
  font-size: 13px;

  span:last-child {
    color: var(--color-text-secondary);
    font-variant-numeric: tabular-nums;
    flex-shrink: 0;
  }
`;

const statusVariant = (
  status: LeadMagnetSummary["status"]
): "success" | "warning" | "neutral" => {
  if (status === "published") return "success";
  if (status === "paused") return "warning";
  return "neutral";
};

const formatDate = (iso?: string): string =>
  iso ? new Date(iso).toLocaleDateString() : "";

const conversion = (m: LeadMagnetSummary): string => {
  const views = m.stats?.views || 0;
  const captures = m.stats?.captures || 0;
  if (!views) return "–";
  return `${Math.round((captures / views) * 100)}%`;
};

const deliveryLabel = (m: LeadMagnetSummary): string => {
  const parts: string[] = [];
  if (m.delivery?.sendEmail) parts.push("Email");
  if (m.delivery?.redirectUrl) parts.push("Redirect");
  if (m.delivery?.enrollWelcome !== false) parts.push("Welcome seq.");
  return parts.length ? parts.join(" + ") : "Unlock in place";
};

// "3 sent · 2 open · 1 click" for magnets that deliver by email.
const emailStatsLabel = (m: LeadMagnetSummary): string => {
  if (!m.delivery?.sendEmail) return "–";
  const sent = m.stats?.emailsDelivered || 0;
  const opens = m.stats?.emailOpens || 0;
  const clicks = m.stats?.emailClicks || 0;
  return `${sent} sent · ${opens} open · ${clicks} click`;
};

const dailyLine = (d: LeadMagnetDailyStat): string => {
  const parts = [
    `${d.views || 0} views`,
    `${d.captures || 0} captures`,
  ];
  if (d.emailsDelivered) parts.push(`${d.emailsDelivered} sent`);
  if (d.emailOpens) parts.push(`${d.emailOpens} opens`);
  if (d.emailClicks) parts.push(`${d.emailClicks} clicks`);
  return parts.join(" · ");
};

type Props = { user: StoreUser | null };

const LeadMagnetsScreen: React.FC<Props> = ({ user }) => {
  const communityId = user?.communityId || "";
  const [magnets, setMagnets] = useState<LeadMagnetSummary[] | null>(null);
  const [error, setError] = useState("");
  const [openMagnetId, setOpenMagnetId] = useState<string | null>(null);
  const [captures, setCaptures] = useState<
    Record<string, { captures: LeadMagnetCapture[]; daily: LeadMagnetDailyStat[] }>
  >({});
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!communityId) return;
    setError("");
    try {
      const result = await getLeadMagnets(communityId);
      setMagnets(result.magnets);
    } catch (err) {
      setError((err as Error)?.message || "Could not load lead magnets.");
      setMagnets([]);
    }
  }, [communityId]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleCaptures = async (magnet: LeadMagnetSummary) => {
    if (openMagnetId === magnet.id) {
      setOpenMagnetId(null);
      return;
    }
    setOpenMagnetId(magnet.id);
    if (!captures[magnet.id]) {
      try {
        const detail = await getLeadMagnetLeads(communityId, magnet.id);
        setCaptures((prev) => ({ ...prev, [magnet.id]: detail }));
      } catch {
        setCaptures((prev) => ({
          ...prev,
          [magnet.id]: { captures: [], daily: [] },
        }));
      }
    }
  };

  const copyLink = (magnet: LeadMagnetSummary, ev: React.MouseEvent) => {
    ev.stopPropagation();
    navigator.clipboard?.writeText(magnet.url);
    setCopiedId(magnet.id);
    window.setTimeout(() => setCopiedId(null), 1500);
  };

  const openPage = (magnet: LeadMagnetSummary, ev: React.MouseEvent) => {
    ev.stopPropagation();
    window.open(magnet.url, "_blank", "noopener");
  };

  const renderRows = () => {
    if (!magnets) {
      return (
        <div style={{ padding: "var(--space-4)" }}>
          {[0, 1, 2].map((i) => (
            <div key={i} style={{ padding: "var(--space-2) 0" }}>
              <Skeleton variant="line" height={20} />
            </div>
          ))}
        </div>
      );
    }
    if (!magnets.length) {
      return (
        <EmptyState
          title="No lead magnets yet"
          description="Ask Claude to create one: a quiz, checklist, guide or calculator gated behind email capture, hosted on your landing site. Captured emails land in your subscriber list automatically."
        />
      );
    }
    return magnets.map((magnet) => (
      <React.Fragment key={magnet.id}>
        <Row onClick={() => toggleCaptures(magnet)}>
          <NameCell>
            <strong>{magnet.title}</strong>
            <span>
              /m/{magnet.id}
              {magnet.description ? ` — ${magnet.description}` : ""}
            </span>
          </NameCell>
          <div>
            <Tag size="sm">{magnet.type}</Tag>
          </div>
          <div>
            <Tag size="sm" variant={statusVariant(magnet.status)} withDot>
              {magnet.status}
            </Tag>
          </div>
          <Num>{magnet.stats?.views || 0}</Num>
          <Num>
            {magnet.stats?.captures || 0}
            {` (${conversion(magnet)})`}
          </Num>
          <div style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>
            {emailStatsLabel(magnet)}
          </div>
          <Actions>
            <Button
              size="sm"
              variant="ghost"
              onClick={(ev) => copyLink(magnet, ev)}
            >
              {copiedId === magnet.id ? "Copied" : "Copy link"}
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={(ev) => openPage(magnet, ev)}
              disabled={magnet.status !== "published"}
            >
              Open
            </Button>
          </Actions>
        </Row>
        {openMagnetId === magnet.id && (
          <CapturesPanel>
            {!captures[magnet.id] ? (
              <Skeleton variant="line" height={16} />
            ) : (
              <>
                <PanelMeta>
                  Delivery: {deliveryLabel(magnet)}
                  {magnet.publishedAt
                    ? ` · Published ${formatDate(magnet.publishedAt)}`
                    : ""}
                </PanelMeta>
                {captures[magnet.id].daily.length > 0 && (
                  <TrendBlock>
                    <strong>Last {captures[magnet.id].daily.length} days</strong>
                    {captures[magnet.id].daily
                      .slice(-14)
                      .reverse()
                      .map((d) => (
                        <CaptureRow key={d.day}>
                          <span>{d.day}</span>
                          <span>{dailyLine(d)}</span>
                        </CaptureRow>
                      ))}
                  </TrendBlock>
                )}
                <strong style={{ fontSize: 13 }}>Captured leads</strong>
                {captures[magnet.id].captures.length ? (
                  captures[magnet.id].captures.map((capture) => (
                    <CaptureRow key={capture.email}>
                      <span>
                        {capture.email}
                        {capture.name ? ` (${capture.name})` : ""}
                      </span>
                      <span>{formatDate(capture.createdAt)}</span>
                    </CaptureRow>
                  ))
                ) : (
                  <CaptureRow>
                    <span>No captures yet.</span>
                  </CaptureRow>
                )}
              </>
            )}
          </CapturesPanel>
        )}
      </React.Fragment>
    ));
  };

  return (
    <DesignSystemSurface>
      <Page>
        <PageInner>
          <PageHeader
            title="Lead Magnets"
            subtitle="Gated assets on your landing site that turn visitors into subscribers. Created and published by Claude through the Tribed MCP; captures flow into your email list."
            actions={
              <Button size="sm" variant="secondary" onClick={load}>
                Refresh
              </Button>
            }
          />
          {error && (
            <Card padding="md">
              <span style={{ color: "var(--color-danger, #c0392b)" }}>
                {error}
              </span>
            </Card>
          )}
          <Card padding="none">
            <TableHead>
              <div>Magnet</div>
              <div>Type</div>
              <div>Status</div>
              <div>Views</div>
              <div>Captures</div>
              <div>Email</div>
              <div />
            </TableHead>
            {renderRows()}
          </Card>
        </PageInner>
      </Page>
    </DesignSystemSurface>
  );
};

const mapStateToProps = (state) => ({
  user: state.storeUser,
});

export default connect(mapStateToProps, null)(LeadMagnetsScreen);
